import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export interface ExtraConfigField {
  key: string;
  label: string;
  type: 'string' | 'number' | 'boolean';
  placeholder?: string;
  description?: string;
}

interface Props {
  fields: ExtraConfigField[];
  value: Record<string, any> | undefined;
  onChange: (next: Record<string, any>) => void;
}

export function ExtraConfigEditor({ fields, value, onChange }: Props) {
  const v = value ?? {};

  const set = (key: string, raw: any) => {
    const next = { ...v };
    if (raw === '' || raw === null || raw === undefined) {
      delete next[key];
    } else {
      next[key] = raw;
    }
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {fields.map((f) => (
        <div key={f.key} className="space-y-1">
          <Label className="text-sm">{f.label}</Label>
          {f.description && <p className="text-xs text-muted-foreground">{f.description}</p>}
          {f.type === 'boolean' ? (
            <Switch
              checked={Boolean(v[f.key])}
              onCheckedChange={(checked) => set(f.key, checked)}
            />
          ) : f.type === 'number' ? (
            <Input
              type="number"
              value={v[f.key] ?? ''}
              placeholder={f.placeholder}
              onChange={(e) => {
                const n = e.target.value === '' ? undefined : Number(e.target.value);
                set(f.key, Number.isNaN(n) ? undefined : n);
              }}
            />
          ) : (
            <Input
              value={v[f.key] ?? ''}
              placeholder={f.placeholder}
              onChange={(e) => set(f.key, e.target.value)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export const S4HANA_EXTRA_FIELDS: ExtraConfigField[] = [
  {
    key: 'descriptionLanguage',
    label: 'Idioma principal de descripción',
    type: 'string',
    placeholder: 'ES',
    description: 'Código ISO de idioma usado para resolver to_Description. Ej: ES, EN, PT.',
  },
  {
    key: 'descriptionLanguageFallback',
    label: 'Idioma de fallback',
    type: 'string',
    placeholder: 'EN',
    description: 'Si no encuentra la descripción en el idioma principal, usa este.',
  },
  {
    key: 'productTypeFilter',
    label: 'Filtrar por ProductType',
    type: 'string',
    placeholder: 'FERT',
    description: 'Solo trae materiales de este tipo. Vacío = todos. Ej: FERT, HALB, ROH.',
  },
  {
    key: 'includeMarkedForDeletion',
    label: 'Incluir marcados para borrado',
    type: 'boolean',
    description: 'Si está apagado, ignora materiales con IsMarkedForDeletion=true.',
  },
  {
    key: 'pageSize',
    label: 'Page size ($top)',
    type: 'number',
    placeholder: '200',
    description: 'Tamaño de página OData. Default 200.',
  },
  {
    key: 'expandDescription',
    label: 'Expandir to_Description',
    type: 'boolean',
    description:
      'Si está apagado, no trae la descripción multilingüe (más rápido pero name = código).',
  },
  {
    key: 'syncProfitCenters',
    label: 'Sincronizar Profit Centers',
    type: 'boolean',
    description: 'Pobla la tabla ProfitCenter desde API_PROFITCENTER_SRV.',
  },
  {
    key: 'syncLobs',
    label: 'Sincronizar LOB / Segment',
    type: 'boolean',
    description:
      'Pobla la tabla LOB desde API_SEGMENT_SRV (entidad A_Segment). No machaca jerarquía ni márgenes locales.',
  },
  {
    key: 'lobServiceUrl',
    label: 'URL service de LOB (override)',
    type: 'string',
    description:
      'Opcional. Si tu tenant SAP usa otro nombre de service (no API_SEGMENT_SRV), poné la URL completa acá.',
  },
  {
    key: 'syncCountries',
    label: 'Sincronizar países',
    type: 'boolean',
    description: 'Pobla la tabla Country desde API_COUNTRY_SRV.',
  },
];

export const HUBSPOT_EXTRA_FIELDS: ExtraConfigField[] = [
  {
    key: 'syncCompanies',
    label: 'Sincronizar Empresas',
    type: 'boolean',
    description: 'Trae Companies desde HubSpot a hubspot_companies.',
  },
  {
    key: 'syncContacts',
    label: 'Sincronizar Contactos',
    type: 'boolean',
    description: 'Trae Contacts desde HubSpot a hubspot_contacts.',
  },
  {
    key: 'syncDeals',
    label: 'Sincronizar Negocios',
    type: 'boolean',
    description: 'Trae Deals desde HubSpot a hubspot_deals.',
  },
];
